import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface DrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  headerContent?: ReactNode;
  headerClassName?: string;
  footerContent?: ReactNode;
  footerClassName?: string;
  hideHeader?: boolean;
  closeButtonAriaLabel: string;
  hideCloseButton?: boolean;
  disableCloseOnOutsideClick?: boolean;
  drawerPosition?: 'right' | 'left' | 'bottom';
  className?: string;
  dialogId?: string;
  wideDrawer?: boolean; // Needs to be safelisted in Tailwind config if used
}

interface DrawerHeaderProps {
  onClose?: () => void;
  headerContent?: ReactNode;
  headerClassName?: string;
  closeButtonAriaLabel: string;
  hideCloseButton?: boolean;
}
interface DrawerContentProps {
  children: ReactNode;
  removePaddingTop?: boolean;
}

interface DrawerFooterProps {
  footerContent?: ReactNode;
  footerClassName?: string;
}

const Drawer = ({
  drawerPosition = 'right',
  className,
  isOpen,
  onClose,
  children,
  headerContent,
  headerClassName,
  footerContent,
  footerClassName,
  hideHeader,
  closeButtonAriaLabel,
  hideCloseButton = false,
  disableCloseOnOutsideClick = false,
  dialogId,
  wideDrawer = false,
}: DrawerProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  // SHOULD BE THE SAME VALUE AS DURATION CLASS ON THE DIALOG. VARIABLE IS NOT USED IN THE CLASS SINCE WE THEN NEED TO SAFELIST IT IN TAILWIND CONFIG
  const ANIMATION_DURATION_TIME = 300;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (isOpen) {
      dialog.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      setTimeout(() => {
        dialog.close();
        document.body.classList.remove('overflow-hidden');
      }, ANIMATION_DURATION_TIME);
    }

    const lockFocusToModal = (event: KeyboardEvent) => {
      if (firstFocusableElement === null || lastFocusableElement === null)
        return;
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            event.preventDefault();
          }
        }
      }
    };
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!disableCloseOnOutsideClick) {
          onClose?.();
        }
      }
    };
    const handleOutsideClick = (event: MouseEvent) => {
      if (disableCloseOnOutsideClick) return;
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };
    setDialogIsOpen(isOpen);

    dialog.addEventListener('click', handleOutsideClick);
    dialog.addEventListener('keydown', lockFocusToModal);
    dialog.addEventListener('keydown', handleEscKeyPress);

    return () => {
      dialog.removeEventListener('click', handleOutsideClick);
      dialog.removeEventListener('keydown', lockFocusToModal);
      dialog.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [disableCloseOnOutsideClick, isOpen, onClose]);

  const drawerFromRight = drawerPosition === 'right';
  const drawerFromLeft = drawerPosition === 'left';
  const drawerFromBottom = drawerPosition === 'bottom';

  return (
    <>
      <dialog
        id={dialogId}
        className={clsx(
          'inset-[unset] my-0 bottom-0 transition-all duration-300 ease-out fixed h-screen max-w-full backdrop:bg-none backdrop:bg-black backdrop:bg-opacity-50',
          wideDrawer ? 'lg:w-[500px]' : 'lg:w-[412px]',
          drawerFromRight &&
            'top-0 right-0 w-full lg:max-w-[calc(100vw-20px)] translate-x-0 max-h-full',
          drawerFromLeft &&
            'top-0 left-0 w-full lg:max-w-[calc(100vw-20px)] translate-x-0 max-h-full',
          drawerFromBottom &&
            'left-0 right-0 mx-0 bottom-0 w-full max-h-[calc(100vh-200px)] h-full translate-y-0 overflow-y-auto',
          drawerFromRight &&
            (dialogIsOpen ? 'translate-x-0' : '!translate-x-full'),
          drawerFromLeft &&
            (dialogIsOpen ? 'translate-x-0' : '!-translate-x-full'),
          drawerFromBottom &&
            (dialogIsOpen ? 'translate-y-0' : '!translate-y-full'),
          className
        )}
        ref={dialogRef}
      >
        <div className="w-full h-full" ref={contentRef}>
          <div className="flex flex-col h-full" ref={contentRef}>
            {!hideHeader && (
              <DrawerHeader
                headerContent={headerContent}
                headerClassName={headerClassName}
                onClose={onClose}
                closeButtonAriaLabel={closeButtonAriaLabel}
                hideCloseButton={hideCloseButton}
              />
            )}
            <DrawerContent removePaddingTop={!hideHeader}>
              {children}
            </DrawerContent>
            {footerContent && (
              <DrawerFooter
                footerContent={footerContent}
                footerClassName={footerClassName}
              />
            )}
          </div>
        </div>
      </dialog>
    </>
  );
};

const DrawerContent: FC<DrawerContentProps> = ({
  children,
  removePaddingTop,
}) => {
  return (
    <div
      className={clsx(
        'flex-1 overflow-hidden flex flex-col',
        removePaddingTop && 'pt-0'
      )}
    >
      {children}
    </div>
  );
};
DrawerContent.displayName = 'DrawerContent';

const DrawerFooter: FC<DrawerFooterProps> = ({
  footerContent,
  footerClassName,
}) => {
  return (
    <footer className={clsx(footerClassName, 'p-4 border-t border-gray-300')}>
      {footerContent}
    </footer>
  );
};
DrawerFooter.displayName = 'DrawerFooter';

const DrawerHeader: FC<DrawerHeaderProps> = ({
  headerContent,
  headerClassName,
  onClose,
  closeButtonAriaLabel,
  hideCloseButton,
}) => {
  return (
    <header
      className={clsx(
        headerClassName,
        'flex pl-4 pb-0',
        headerContent ? 'justify-between items-center gap-4' : 'justify-end'
      )}
    >
      {headerContent}
      {hideCloseButton ? null : (
        <button
          aria-label={closeButtonAriaLabel}
          className="p-4 text-md"
          onClick={onClose}
        >
          <span className="text-sm">Replace with Close Icon</span>
        </button>
      )}
    </header>
  );
};
DrawerHeader.displayName = 'DrawerHeader';

export default Drawer;
